import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Race, ScoredRace } from '../../types/races';
import { processSystemRaces, countUniqueVenues } from '../../utils/systemRaceProcessing';
import SystemRaceCard from './SystemRaceCard';

export default function SystemTodaysRaces() {
  const [races, setRaces] = useState<ScoredRace[]>([]);
  const [loadingRaces, setLoadingRaces] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRaces, setExpandedRaces] = useState<{ [key: string]: boolean }>({});
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchTodaysRaces = async () => {
    try {
      setLoadingRaces(true);
      const response = await api.get('/api/system/todays-races');
      setRaces(processSystemRaces(response.data));
      setError(null);
    } catch (err: any) {
      console.error('Error fetching races:', err);
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? errorDetail.msg || JSON.stringify(errorDetail)
        : errorDetail || err.message || 'Failed to fetch races';
      setError(errorMessage);
    } finally {
      setLoadingRaces(false);
    }
  };

  useEffect(() => {
    fetchTodaysRaces();
  }, []);

  const toggleAllRaces = (expand: boolean) => {
    setExpandedRaces(prev => {
      const newState = { ...prev };
      races.forEach(race => {
        newState[race.marketId] = expand;
      });
      return newState;
    });
  };

  const toggleRaceExpanded = (marketId: string) => {
    setExpandedRaces(prev => ({
      ...prev,
      [marketId]: !prev[marketId]
    }));
  };

  const toggleQualifyingRaces = () => {
    setExpandedRaces(prev => {
      const newState = { ...prev };
      races.forEach(race => {
        if (race.tag === 'Qualifying Race') {
          newState[race.marketId] = true;
        }
      });
      return newState;
    });
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-col space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-medium text-gray-900">Today's Races</h2>
              <svg 
                className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {isExpanded && races.length > 0 && (
            <div className="flex justify-between items-center">
              {/* Desktop controls */}
              <div className="hidden sm:flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm">
                  <button
                    onClick={() => toggleQualifyingRaces()}
                    className="inline-flex items-center px-2 py-1 text-gray-600 hover:text-gray-900"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Expand Qualifying
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => toggleAllRaces(true)}
                    className="inline-flex items-center px-2 py-1 text-gray-600 hover:text-gray-900"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7m14 14H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
                    </svg>
                    Expand All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => toggleAllRaces(false)}
                    className="inline-flex items-center px-2 py-1 text-gray-600 hover:text-gray-900"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7m-14-4h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2z" />
                    </svg>
                    Collapse All
                  </button>
                </div>
                <button
                  onClick={fetchTodaysRaces}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              {/* Mobile controls */}
              <div className="flex sm:hidden items-center space-x-2">
                <button
                  onClick={() => toggleAllRaces(true)}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  aria-label="Expand All"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                  </svg>
                </button>
                <button
                  onClick={() => toggleAllRaces(false)}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  aria-label="Collapse All"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l-4-4m0 8l4-4" />
                  </svg>
                </button>
                <button
                  onClick={fetchTodaysRaces}
                  className="p-2 rounded-full bg-primary-100 hover:bg-primary-200 text-primary-700"
                  aria-label="Refresh Races"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Race Meetings: {countUniqueVenues(races)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>Races: {races.length}</span>
          </div>
          <div className="flex items-center space-x-2">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Qualifying: {races.filter(race => race.tag === 'Qualifying Race').length}</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        loadingRaces ? (
          <div className="animate-pulse space-y-4 mt-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-gray-50 p-4 rounded-lg">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : races.length > 0 ? (
          <div className="space-y-4 mt-4">
            {races.map((race) => (
              <SystemRaceCard
                key={race.marketId}
                race={race}
                isExpanded={!!expandedRaces[race.marketId]}
                onToggle={() => toggleRaceExpanded(race.marketId)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            No races found for today
          </div>
        )
      )}
    </div>
  );
} 